import { defineStore } from "pinia";
import { TopolSpecialBlockTranslation } from "@/schemas/template/languageSchema";
export interface SyncedTranslationEntry {
  fullTranslation: TopolSpecialBlockTranslation;
  pickedLang?: string;
}

interface State {
  entries: Record<string, SyncedTranslationEntry>;
}

export function remapTranslationUids(
  translation: TopolSpecialBlockTranslation | undefined,
  uidMap: Record<string, string>
): TopolSpecialBlockTranslation {
  const remapped: TopolSpecialBlockTranslation = {};
  if (!translation) {
    return remapped;
  }
  for (const langKey of Object.keys(translation)) {
    remapped[langKey] = {};
    const langTranslations = translation[langKey];
    if (!langTranslations || typeof langTranslations !== "object") {
      continue;
    }
    for (const oldUid of Object.keys(langTranslations)) {
      const newUid = uidMap[oldUid];
      if (newUid) {
        remapped[langKey][newUid] = langTranslations[oldUid];
      }
    }
  }
  return remapped;
}

export function buildSectionTranslationForSave({
  preserved,
  langs,
  blocks,
  pickedLang,
  isTranslatable,
  getPrimaryTranslation,
}: {
  preserved: TopolSpecialBlockTranslation;
  langs?: { key: string; translations: Record<string, any> }[] | null;
  blocks: { uid: string; [key: string]: any }[];
  pickedLang?: string;
  isTranslatable: (block: any) => boolean;
  getPrimaryTranslation: (block: any) => any;
}): TopolSpecialBlockTranslation | undefined {
  const translations = preserved;
  const hasLangs = !!langs && langs.length > 0;
  const currentBlockUids = new Set<string>();

  if (hasLangs) {
    for (const lang of langs!) {
      translations[lang.key] = {};
    }
    for (const block of blocks) {
      currentBlockUids.add(block.uid);
      if (isTranslatable(block)) {
        for (const lang of langs!) {
          if (lang.translations[block.uid]) {
            translations[lang.key][block.uid] = lang.translations[block.uid];
          }
        }
      }
    }
  } else {
    if (Object.keys(translations).length === 0) {
      return undefined;
    }
    for (const block of blocks) {
      currentBlockUids.add(block.uid);
      if (pickedLang && isTranslatable(block)) {
        if (!translations[pickedLang]) {
          translations[pickedLang] = {};
        }
        translations[pickedLang][block.uid] = getPrimaryTranslation(block);
      }
    }
  }

  for (const langKey of Object.keys(translations)) {
    for (const uid of Object.keys(translations[langKey])) {
      if (!currentBlockUids.has(uid)) {
        delete translations[langKey][uid];
      }
    }
  }

  if (Object.keys(translations).length === 0) {
    return undefined;
  }
  return translations;
}

export const useSyncedTranslationsStore = defineStore("syncedTranslations", {
  state: (): State => ({
    entries: {},
  }),

  getters: {
    getSectionTranslation:
      (state) =>
      (sectionUid: string): SyncedTranslationEntry | undefined =>
        state.entries[sectionUid],
  },

  actions: {
    setSectionTranslation(sectionUid: string, entry: SyncedTranslationEntry) {
      this.entries[sectionUid] = entry;
    },
    deleteSectionTranslation(sectionUid: string) {
      delete this.entries[sectionUid];
    },
    clear() {
      this.entries = {};
    },
  },
});
